import { Calendar, MapPin } from 'lucide-react'
import { useLocation } from 'wouter'
import { getImageUrls } from '@/utils/imageUrls'
import defaultImage from '@/assets/default-image.svg'
import { LazyLoadImage } from 'react-lazy-load-image-component'

export interface Event {
  id: number | string
  heading: string
  page_title?: string
  page_description?: string
  content?: string
  date_start?: string
  date_end?: string
  event_dates?: string
  location?: string
  price_text?: string
  filename?: string
  filename_alt?: string
  page_url?: string
  published_date?: string
  category_id?: number | null
}

interface EventCardProps {
  event: Event
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '')
}

function truncate(text: string, maxLength: number): string {
  const stripped = stripHtml(text)
  if (stripped.length <= maxLength) return stripped
  return stripped.slice(0, maxLength) + '...'
}

export default function EventCard({ event }: EventCardProps) {
  const [, navigate] = useLocation()

  const handleClick = () => {
    navigate(`/events/${event.id}`)
  }

  const imageUrls = event.filename
    ? getImageUrls('events', event.filename)
    : null

  const formattedDate = event.date_start
    ? new Date(event.date_start).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
    : event.event_dates

  const preview = event.page_description
    ? truncate(event.page_description, 80)
    : null

  return (
    <div
      onClick={handleClick}
      className="relative cursor-pointer group bg-[#212124] aspect-square border border-[#464646] rounded-[22px] overflow-hidden"
    >
      {imageUrls ? (
        <div className='relative w-full h-full'>
          <LazyLoadImage
            src={imageUrls.squareThumb}
            alt={event.filename_alt || event.heading}
            effect="opacity"
            wrapperClassName="lazy-image-wrapper"
            className="
              w-full h-full object-cover
              transition-transform duration-300
              group-hover:scale-105
            "
          />
         <div className="absolute inset-0 backdrop-blur-[16px] bg-[linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,1)_10%,rgba(0,0,0,0)_100%)]" style={{
            WebkitMaskImage:
              'linear-gradient(to bottom, transparent 50.6%, black 100%)',
            maskImage:
              'linear-gradient(to bottom, transparent 50.6%, black 100%)',
          }} />
        </div>
      ) : (
        <div className="absolute inset-0 bg-[#212124] flex items-center justify-center">
          <img src={defaultImage} alt="Default News" />
        </div>
      )}

      {/* Event Content */}
      <div className="absolute bottom-0 left-0 right-0 px-2 py-[9px] text-white flex flex-col justify-end h-full">
        <div className="mt-auto">
          {/* Title */}
          <h3 className="font-semibold text-sm font-figtree line-clamp-2">
            {event.heading}
          </h3>

          {/* Description */}
          {preview && (
            <p className="text-[#ffffff] text-[12px] line-clamp-4 font-figtree font-normal leading-[14px]">
              {preview}
            </p>
          )}

          {/* Gradient Divider */}
          <div className="h-px bg-[linear-gradient(90deg,rgba(255,255,255,0.05)_0%,#B1B1B1_49.83%,rgba(255,255,255,0.05)_100.62%)]
 mt-2 mb-1" />

          {/* Meta Info */}
          <div className="flex items-center justify-between gap-4 p-1 text-white text-[12px] font-figtree font-medium">
            {formattedDate && (
              <div className="flex items-center gap-1">
                <span className='text-[10px] leading-[12px] text-[#ffffff] font-medium font-figtree'>{formattedDate}</span>
              </div>
            )}

            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              <span className="line-clamp-1 text-[10px] leading-[12px] font-medium font-figtree">
                {event.location || 'Secrets Hideaway'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
